/**
 * QC Orders List Page — Issue #345.
 *
 * Refactored to use the shared `AdminDataTable` so the page picks up advanced
 * search, column visibility, loading/empty/error states and a permission-aware
 * row action menu that drives the QC order state machine through the existing
 * POST /api/v1/lab/qc-orders/{id}/status endpoint. QC orders are
 * laboratory-grade audit records — the only non-physical removal semantic
 * the backend exposes is the `cancelled` state, so 取消 is wired to that
 * transition (no physical delete).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { Ban, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  useQcOrdersList,
  useQcOrderTransitionById,
  type QcOrderView,
} from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { canReadQcOrder, canUpdateQcOrder } from '@/lib/labPermissions';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

type QcStatus =
  | 'pending'
  | 'in_progress'
  | 'passed'
  | 'failed'
  | 'retest_required'
  | 'cancelled';

/**
 * Issue #345 audit fix — mirror packages/domain/src/lab/qc-state-machine.ts so
 * the row-action menu only exposes legal next states. `passed` / `failed` /
 * `cancelled` are terminal; offering 取消 on those rows would attempt an
 * illegal transition that the backend would 409-reject anyway, but the UI
 * must not advertise it. Kept in-file so admin-web has no build-time
 * dependency on @jinzhuan/domain (existing convention).
 */
const QC_LEGAL_NEXT: Readonly<Record<QcStatus, ReadonlyArray<QcStatus>>> = {
  pending: ['in_progress', 'cancelled'],
  in_progress: ['passed', 'failed', 'retest_required', 'cancelled'],
  retest_required: ['in_progress', 'cancelled'],
  passed: [],
  failed: [],
  cancelled: [],
};
function canQcTransition(from: string, to: QcStatus): boolean {
  const next = QC_LEGAL_NEXT[from as QcStatus];
  return next ? next.includes(to) : false;
}

const QC_STATUS_OPTIONS: ReadonlyArray<{ value: QcStatus; label: string }> = [
  { value: 'pending', label: '待处理' },
  { value: 'in_progress', label: '进行中' },
  { value: 'passed', label: '已通过' },
  { value: 'failed', label: '已失败' },
  { value: 'retest_required', label: '需重检' },
  { value: 'cancelled', label: '已取消' },
];

function getStatusLabel(status: string): string {
  return QC_STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-900',
    in_progress: 'bg-blue-100 text-blue-900',
    passed: 'bg-green-100 text-green-900',
    failed: 'bg-red-100 text-red-900',
    retest_required: 'bg-yellow-100 text-yellow-900',
    cancelled: 'bg-gray-100 text-gray-900',
  };
  return colors[status] || 'bg-gray-100 text-gray-900';
}

export default function QcOrdersListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch, error } = useQcOrdersList();
  const { transition, loading: transitioning } = useQcOrderTransitionById();

  const [orders, setOrders] = useState<QcOrderView[]>([]);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    cellBatchId: '',
    status: '',
  });

  const limit = 20;
  const statusFilter = searchValues['status'] ?? '';
  const cellBatchFilter = (searchValues['cellBatchId'] ?? '').trim();

  const canRead = canReadQcOrder(permissions);
  const canUpdate = canUpdateQcOrder(permissions);

  const refresh = useCallback(async () => {
    if (!canRead) return;
    setPageLoading(true);
    try {
      const data = await fetch({
        page,
        limit,
        ...(statusFilter ? { status: statusFilter as QcStatus } : {}),
        ...(cellBatchFilter ? { cellBatchId: cellBatchFilter } : {}),
      });
      if (data) setOrders(data.items || []);
    } catch {
      toast.error(`加载质检单失败：${error?.message ?? '未知错误'}`);
    } finally {
      setPageLoading(false);
    }
  }, [canRead, cellBatchFilter, error?.message, fetch, page, statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const columns: ReadonlyArray<AdminDataTableColumn<QcOrderView>> = useMemo(
    () => [
      {
        key: 'id',
        header: '质检单 ID',
        alwaysVisible: true,
        render: (o) => <span className="font-mono text-sm">{o.id}</span>,
      },
      {
        key: 'cellBatchId',
        header: '批次 ID',
        render: (o) => <span className="font-mono text-sm">{o.cellBatchId}</span>,
      },
      {
        key: 'status',
        header: '状态',
        render: (o) => (
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(o.status)}`}
          >
            {getStatusLabel(o.status)}
          </span>
        ),
      },
      {
        key: 'version',
        header: '版本',
        defaultHidden: true,
        render: (o) => `v${o.version}`,
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (o) => new Date(o.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<QcOrderView>> = useMemo(
    () => [
      {
        key: 'view',
        label: '查看详情',
        icon: <Eye className="h-3.5 w-3.5" />,
        onSelect: (o) => navigate(`/lab/qc/${o.id}`),
      },
      {
        key: 'edit',
        label: '更新状态',
        icon: <Pencil className="h-3.5 w-3.5" />,
        visible: () => canUpdate,
        onSelect: (o) => navigate(`/lab/qc/${o.id}`),
      },
      {
        key: 'cancel',
        label: '取消（标记 cancelled）',
        icon: <Ban className="h-3.5 w-3.5" />,
        destructive: true,
        // 实验室质检单不可物理删除（AI GUIDE §6）。取消 = 状态机迁移到
        // cancelled，由后端版本校验保护；前端 confirm 同时提示审计影响。
        // Issue #345 audit fix：只对状态机允许 -> cancelled 的源态显示该动作，
        // passed / failed / cancelled 等终态不显示，避免向用户暴露非法转换。
        visible: (o) => canUpdate && canQcTransition(o.status, 'cancelled'),
        disabled: () => transitioning,
        onSelect: async (o) => {
          const confirmed = window.confirm(
            `将质检单「${o.id}」标记为已取消。该操作不会物理删除任何质控历史；操作会写入审计日志，是否继续？`,
          );
          if (!confirmed) return;
          const ok = await transition(o.id, 'cancelled', o.version);
          if (ok) {
            toast.success('质检单已取消');
            await refresh();
          }
        },
      },
    ],
    [canUpdate, navigate, refresh, transition, transitioning],
  );

  if (!canRead) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看质检单的权限</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">质检单管理</h1>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          刷新
        </Button>
      </div>

      <AdminDataTable<QcOrderView>
        testIdPrefix="qc-orders"
        columns={columns}
        rows={orders}
        isLoading={pageLoading}
        emptyMessage="暂无质检单数据"
        filters={{
          fields: [
            {
              kind: 'text',
              name: 'cellBatchId',
              label: '批次 ID',
              placeholder: '按批次 ID 精确搜索',
            },
            {
              kind: 'select',
              name: 'status',
              label: '状态',
              options: QC_STATUS_OPTIONS.map(({ value, label }) => ({
                value,
                label,
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ cellBatchId: '', status: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          // The list endpoint returns `items` but does not surface a `total`
          // that satisfies AdminDataTable's "page * pageSize >= total" disable
          // rule. We use a generous sentinel until the backend follow-up adds
          // a total field; AdminDataTable falls back to "items.length only"
          // semantics so 下一页 remains usable as long as data is returned.
          total: orders.length === limit ? page * limit + 1 : (page - 1) * limit + orders.length,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
